import React, { ChangeEvent, useCallback } from 'react';
import { Card, CardGroup, Col, Row, Spinner } from 'react-bootstrap';
import { format, isValid, parseISO } from 'date-fns';

import { InputMoney, InputPercent } from '~/components/NovosInputs';
import { formatCurrencyAsText } from '~/utils/functions';

import { useAlteracaoPrecoEntradaFornecedor } from '../../AlteracaoPrecoEntradaFornecedorContext';
import { CardCusto } from './CardCusto';
import { CustomButtonNovo } from '~/components/Buttons/CustomButtonNovo';

const HEADER_STYLE = {
  textAlign: 'center' as const,
};

const HEADER_COLORS = {
  margem: '#d0d6f2',
  venda: '#ffe6c0',
  icms: '#CBCCCF',
  simulador: '#b7e5c1',
};

const formatNumericText = (value?: number | string | null): string =>
  formatCurrencyAsText(Number(value ?? 0));

const formatPercentReference = (value?: number | string | null): string =>
  Number(value ?? 0).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDateValue = (value?: string | Date | null): string => {
  if (!value) return '-';

  const parsedDate =
    typeof value === 'string' ? parseISO(value) : new Date(value);

  return isValid(parsedDate) ? format(parsedDate, 'dd/MM/yyyy') : '-';
};

const getTextValue = (value?: string | null): string => value?.trim() || '-';

const Cards: React.FC = () => {
  const {
    register,
    control,
    errors,
    loadingCalc,
    simulador,
    assumeValor,
    selectedRow,
  } = useAlteracaoPrecoEntradaFornecedor();

  const item = selectedRow;
  const hasSelectedRow = Boolean(item);

  const handleMarkupInput = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (!item || event.target.value.length > 6) return;

      simulador('Mark-up', event, item);
    },
    [item, simulador],
  );

  const handlePrecoVendaInput = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (!item) return;

      simulador('precoVenda', event, item);
    },
    [item, simulador],
  );

  const handleAssumeValor = useCallback(() => {
    if (!item) return;

    assumeValor();
  }, [assumeValor, item]);

  if (!hasSelectedRow) return null;

  return (
    <Row className="mt-3">
      <Col sm={12}>
        <CardGroup>
          <CardCusto item={selectedRow} />

          <Card className="margemContainer">
            <Card.Header
              style={{ ...HEADER_STYLE, backgroundColor: HEADER_COLORS.margem }}
            >
              Margem
            </Card.Header>

            <Card.Body>
              <Card.Text>
                Referência: {formatPercentReference(item?.val_margem)}%
              </Card.Text>

              <div className="d-flex justify-content-between align-items-center">
                <Card.Text className="mb-0">
                  Calculada: {item?.calcMargVda || '0,00'}%
                </Card.Text>

                {loadingCalc && (
                  <div aria-live="polite" aria-label="Calculando margem">
                    <Spinner animation="border" size="sm" />
                  </div>
                )}
              </div>

              <Card.Text className="mt-2">
                Atual: {formatNumericText(item?.calcMargVdaAtual)}%
              </Card.Text>
            </Card.Body>
          </Card>

          <Card className="vendaContainer" style={{ maxWidth: '25%' }}>
            <Card.Header
              style={{ ...HEADER_STYLE, backgroundColor: HEADER_COLORS.venda }}
            >
              Venda
            </Card.Header>

            <Card.Body>
              <Card.Text>
                Diverg. Sug: {formatNumericText(item?.divergenciaSugestao)}%
              </Card.Text>

              <Card.Text className="mt-n2">
                Dta. Alt. Venda: {formatDateValue(item?.dta_ult_alt_preco_base)}
              </Card.Text>

              <Card.Text className="mt-n2">
                Anterior: {formatNumericText(item?.val_anterior_preco_base)}
              </Card.Text>
            </Card.Body>
          </Card>

          <Card className="icmsRedContainer">
            <Card.Header
              style={{ ...HEADER_STYLE, backgroundColor: HEADER_COLORS.icms }}
            >
              ICMS e Red. BC.
            </Card.Header>

            <Card.Body>
              <div className="text-container mb-2">
                <strong>Entr:</strong>{' '}
                {getTextValue(item?.des_info_icms_entrada)}
              </div>

              <div className="text-container">
                <strong>Saída:</strong>{' '}
                {getTextValue(item?.des_info_icms_saida)}
              </div>
            </Card.Body>
          </Card>

          <Card className="simuladorContainer">
            <Card.Header
              style={{
                ...HEADER_STYLE,
                backgroundColor: HEADER_COLORS.simulador,
              }}
            >
              Simulador
            </Card.Header>

            <Card.Body>
              <section
                className="section-input"
                aria-label="Simulador de venda"
              >
                <div className="text-content">
                  <Card.Text>Mark-up:</Card.Text>

                  <span className="text-preco">
                    <Card.Text>Preço Venda:</Card.Text>
                  </span>
                </div>

                <div className="containerInput">
                  <InputPercent
                    label=""
                    placeholder=""
                    max={100}
                    min={0}
                    name="markUp"
                    onInput={handleMarkupInput}
                    showIcon
                    register={register}
                    control={control}
                    isError={!!errors.markUp}
                  />

                  <InputMoney
                    className="form-control inputMoney"
                    label=""
                    placeholder=""
                    onInput={handlePrecoVendaInput}
                    max={9999}
                    min={0}
                    name="precoVenda"
                    register={register}
                    control={control}
                    showIcon
                    isError={!!errors.precoVenda}
                  />
                </div>
              </section>

              <CustomButtonNovo
                onClick={handleAssumeValor}
                disabled={!hasSelectedRow}
                ariaLabel="Assumir valor simulado"
                label="Assume valor"
                variant="confirm"
                style={{ marginTop: '10px' }}
                width="100%"
              />
            </Card.Body>
          </Card>
        </CardGroup>
      </Col>
    </Row>
  );
};

export default Cards;
